import { useEffect, useState } from 'react';
import { useCart } from 'react-use-cart';
import sanityFetch from './sanity.fetch';
import type { ProductCard, PurchaseEligibilitySource } from '@/global/types';
import { COURSE_PURCHASE_GATE_FILTER, PRODUCT_CARD_QUERY, PRODUCT_PURCHASE_ELIGIBILITY_SOURCE_QUERY } from 'src/global/constants';
import { resolveProductCardShippingInfo, shippingModeRequiresDelivery } from './resolve-shipping-mode';
import { resolveProductCardShipmentDeclaredValue } from './resolve-shipment-declared-value';
import { normalizePurchaseEligibility } from './product-purchase-eligibility';
import type { Item } from 'react-use-cart';

type CartItemWithGiftData = Item & Pick<ProductCard, 'giftData' | 'voucherData'>;

export const useCartItems = () => {
  const { items: rawCart, updateItemQuantity, updateItem, removeItem, totalItems, totalUniqueItems } = useCart();
  const [fetchedItems, setFetchedItems] = useState<ProductCard[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  useEffect(() => {
    const fetchCartItems = async () => {
      setLoading(true);
      try {
        const res = await sanityFetch<Array<ProductCard & { purchaseEligibilitySources?: PurchaseEligibilitySource[] | null }>>({
          query: `
            *[(_type == 'product' || _type == 'course' || _type == 'bundle' || _type == 'voucher') && _id in $id]{
              ${PRODUCT_CARD_QUERY}
              "purchaseEligibilitySources": *[${COURSE_PURCHASE_GATE_FILTER}]{
                ${PRODUCT_PURCHASE_ELIGIBILITY_SOURCE_QUERY}
              }
            }
          `,
          params: {
            id: rawCart?.map((el) => el.product) || [],
          },
          tags: ['product', 'course', 'bundle', 'voucher'],
        });

        const newArr = rawCart
          .filter((el) => el.quantity && Number(el.quantity) > 0)
          .filter((el) => {
            const item = res.find((item) => item._id === el.product)!;

            if (!item) {
              removeItem(el.id);
              return false;
            }

            if (item._type === 'course' || item._type === 'bundle' || item._type === 'voucher') return true;

            const variant = item.variants?.find((v) => v._id === el.variant) || null;

            if (variant) {
              // check if quantity is not higher than 0
              return variant.countInStock > 0;
            }

            // check if quantity is not higher than 0
            return item.countInStock! > 0;
          })
          .filter((el) => res.find((item) => item._id === el.product)) // double check if we delete some items on previous step
          .map((el) => {
            const item = res.find((item) => item._id === el.product)!;

            const variant = item.variants?.find((v) => v._id === el.variant) || null;

            // check if quantity is not higher than countInStock
            const isNonQuantifiable = item._type === 'course' || item._type === 'bundle' || item._type === 'voucher';
            const quantity = isNonQuantifiable
              ? 1
              : variant
                ? Math.min(el.quantity!, variant.countInStock)
                : Math.min(el.quantity!, item.countInStock!);

            const rawLine = el as CartItemWithGiftData;

            if (item._type === 'voucher') {
              const voucherData = rawLine.voucherData!;
              const shippingInfo = resolveProductCardShippingInfo({
                ...item,
                voucherData,
              });
              const shipmentDeclaredValue = resolveProductCardShipmentDeclaredValue({
                ...item,
                voucherData,
                price: voucherData.amount!,
                quantity,
              });

              return {
                ...item,
                cartItemId: el.id,
                quantity: quantity,
                price: voucherData.amount!,
                voucherData,
                variant: null,
                variants: null,
                shippingMode: shippingInfo.mode,
                shippingLabel: shippingInfo.label,
                shipmentDeclaredValue: shipmentDeclaredValue.value,
                shipmentDeclaredValueSource: shipmentDeclaredValue.source,
                needDelivery: shippingModeRequiresDelivery(shippingInfo.mode),
                purchaseEligibility: normalizePurchaseEligibility(item.purchaseEligibilitySources),
              };
            }

            const shippingInfo = resolveProductCardShippingInfo({
              ...item,
              giftData: rawLine.giftData,
            });
            const shipmentDeclaredValue = resolveProductCardShipmentDeclaredValue({
              ...item,
              giftData: rawLine.giftData,
              price: variant ? variant.price! : item.price!,
              discount: variant ? variant.discount : item.discount,
              quantity,
            });

            return {
              ...item,
              cartItemId: el.id,
              quantity: quantity,
              price: variant ? variant.price! : item.price!,
              discount: variant ? variant.discount : item.discount,
              giftData: rawLine.giftData,
              variant: variant,
              variants: variant ? item.variants : [],
              shippingMode: shippingInfo.mode,
              shippingLabel: shippingInfo.label,
              shipmentDeclaredValue: shipmentDeclaredValue.value,
              shipmentDeclaredValueSource: shipmentDeclaredValue.source,
              needDelivery: shippingModeRequiresDelivery(shippingInfo.mode),
              purchaseEligibility: normalizePurchaseEligibility(item.purchaseEligibilitySources),
            };
          });

        rawCart.forEach((el) => {
          if (
            !res.find((item) => item._id === el.product) || // check if product still exists
            !newArr.find((item) => item._id === el.product) // check if product isn't filtrated in newArr
          ) {
            removeItem(el.id);
          }
        });

        setFetchedItems(newArr);
      } catch (error) {
        console.error(error);
        // TODO: handle error
      } finally {
        setLoading(false);
      }
    };
    if (rawCart?.length > 0 && fetchedItems?.length !== rawCart?.length) {
      fetchCartItems();
    } else if (fetchedItems) {
      // revalidate quantity
      const newArr = fetchedItems
        .filter((_, i) => rawCart[i]?.quantity && rawCart[i]!.quantity! > 0)
        .filter((el) => {
          return !!rawCart.find((item) => {
            const cartItemId = el.cartItemId ?? (el.variant ? el._id + 'variant:' + el.variant._id : el._id);
            return item.id === cartItemId;
          })!;
        })
        .map((el) => {
          const itemInRawCart = rawCart.find((item) => {
            const cartItemId = el.cartItemId ?? (el.variant ? el._id + 'variant:' + el.variant._id : el._id);
            return item.id === cartItemId;
          })!;

          // Apply the same quantity validation logic as in initial processing
          const isNonQuantifiable = el._type === 'course' || el._type === 'bundle' || el._type === 'voucher';
          const validatedQuantity = isNonQuantifiable
            ? 1
            : el.variant
              ? Math.min(itemInRawCart.quantity!, el.variant.countInStock)
              : Math.min(itemInRawCart.quantity!, el.countInStock!);

          return {
            ...el,
            cartItemId: itemInRawCart.id,
            giftData: (itemInRawCart as CartItemWithGiftData).giftData,
            quantity: validatedQuantity,
          };
        });

      rawCart.forEach((el) => {
        if (
          !newArr.find((item) => item._id === el.product) // check if product isn't filtrated in newArr
        ) {
          removeItem(el.id);
        }
      });

      setFetchedItems(newArr);
      // Normalize react-use-cart quantities for non-quantifiable items to avoid double-add effects
      newArr.forEach((item) => {
        if (item._type === 'course' || item._type === 'bundle' || item._type === 'voucher') {
          const productId = item.cartItemId ?? (item.variant ? item._id + 'variant:' + item.variant._id : item._id);
          const raw = rawCart.find((r) => r.id === productId);
          if (raw && (raw.quantity ?? 1) !== 1) {
            updateItemQuantity(productId, 1);
          }
        }
      });
      setLoading(false);
    } else {
      setLoading(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawCart]);

  return {
    cart: rawCart,
    fetchedItems,
    updateItemQuantity,
    updateItem,
    removeItem,
    loading,
    totalItems,
    totalUniqueItems,
  };
};
